<template>
    <BaseModal
        :name="props.name"
        :popover="true"
        :isLoading="!data.ready"
        class=""
    >
        <template v-slot:title v-if="data.ready && data.playlist">
            <PlaylistPreview :item="data.playlist" :row="true" :invert="true" />
        </template>
        <template v-slot:content v-if="data.ready">
            <div class="items-list" v-if="data.tracks.length">
                <draggable
                    v-model="data.tracks"
                    :item-key="(item) => item.id || item.localId"
                    tag="ul"
                    handle=".sort-button"
                    ghost-class="dragging"
                    @end="onSortEnd"
                    class="items"
                >
                    <template #item="{ element: item }">
                        <li :class="{ opened: player.panes.default?._item?.id === item.id }">
                            <TrackPreview :item="item" :invert="true"
                                :light="true"
                                @play="onTrackPlay"
                            />

                            <IconButton icon="close" sprite="navigation"
                                class="tertiary size-m invert"
                                :disabled="!canEdit"
                                @click="remove(item)"
                            />
                            <Button icon="drag" sprite="navigation"
                                v-if="canEdit"
                                class="tertiary submenu size-m sort-button invert" />
                        </li>
                    </template>
                </draggable>
            </div>

            <EmptyLabel
                v-else
                class="empty"
                :invert="true"
                :title="t(`pages.tracks.empty.title`)"
                :description="t(`pages.tracks.empty.description`)"
            />
        </template>
        <template v-slot:footer v-if="data.ready && data.playlist && canEdit">
            <KebabMenu
                icon="plus" sprite="actions"
                v-if="true"
                :leftAuto="locale.rtl"
                :rightAuto="!locale.rtl"
                :tertiary="true"
                :invert="true"
                size="size-m"
                @toggle="data.addKebabActive = !data.addKebabActive"
            >
                <template v-slot:trigger>
                    <IconButton 
                        icon="plus" sprite="actions"
                        :class="{
                            active: data.addKebabActive,
                            invert: true,
                            tertiary: true,
                            'size-s': true
                        }"
                    />
                </template>
                <template v-slot>
                    <ul>
                        <li @click="selectToBeginning">
                            <Icon icon="plus" sprite="actions" />
                            <span>{{ t('modals.playlist.add.beginning') }}</span>
                        </li>
                        <li @click="selectToEnd">
                            <Icon icon="plus" sprite="actions" />
                            <span>{{ t('modals.playlist.add.end') }}</span>
                        </li>
                    </ul>
                </template>
            </KebabMenu>

            <!-- <IconButton icon="plus" sprite="actions" class="tertiary invert size-s" @click="selectTracks" /> -->
        </template>
    </BaseModal>
</template>

<script setup>
import { onMounted, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import draggable from 'vuedraggable'

import BaseModal from '🔗/components/modals/BaseModal.vue';
import Button from '🔗/components/ui/Button.vue';
import IconButton from '🔗/components/ui/IconButton.vue';
import PlaylistPreview from '🔗/components/ui/PlaylistPreview.vue';
import KebabMenu from '🔗/components/ui/KebabMenu.vue';
import EmptyLabel from '🔗/components/ui/EmptyLabel.vue';
import TrackPreview from '🔗/components/ui/TrackPreview.vue';
import { delay } from '🔗/utils/utils';

// import api from '🔗/api';
// import stores from '@/stores';

const player = stores.player();
const locale = stores.locale();
const modals = stores.modals();

const { t } = useI18n();

const props = defineProps({
    name: {
        type: String,
        default: 'playlist'
    }
});

const data = reactive({
    ready: false,
    playlist: null,
    tracks: [],
});

const canEdit = computed(() => {
    return data.playlist && api.playlists().canEdit({ playlist: data.playlist });
});

const onTrackPlay = (track) => {
    player.queue.create(data.tracks, { type: 'playlist', playlist: data.playlist });
}

const onSortEnd = async (evt) => {
    // const payload = {
    //     items: data.tracks.slice(Math.min(evt.newIndex, evt.oldIndex), Math.max(evt.newIndex, evt.oldIndex) + 1),
    //     fromIndex: Math.min(evt.oldIndex, evt.newIndex) + 1
    // }

    data.tracks.forEach((item, id) => {
        if (item.position !== id + 1) {
            if (id === evt.newIndex) {
                api.playlists().tracks.setPosition({
                    playlistId: data.id,
                    trackId: item.id,
                    position: id + 1
                });
            }

            item.position = id + 1;
        }
    });
}

const close = () => {
    modals.close(props.name);
}

const load = async () => {
    if (!data.id) {
        close();
        return;
    }

    data.ready = false;

    data.id = modals.getData(props.name);
    if (!data.id) {
        close();
    }

    data.playlist = await api.playlists().get(data.id);

    const payload = await api.playlists().tracks.list(data.id) || [];

    data.tracks = payload?.items || [];

    if (!data.playlist) {
        close();
    }

    await delay(200);

    data.ready = true;
}

const remove = async (item) => {
    await api.playlists().tracks.remove({
        playlistId: data.id,
        trackId: item?.id
    });

    data.tracks.splice(data.tracks.indexOf(item), 1);
    // checkPosition();
}

// Add
    const add = (payload, mode) => {
        if (!payload?.id) {
            return;
        }

        if (data.tracks.find(item => item.id === payload.id)) {
            return;
        }

        if (mode === 'beginning') {
            data.tracks.unshift(payload);
        } else {
            data.tracks.push(payload);
        }
        // checkPosition();
    }

    const addTrack = async (payload, mode) => {
        await api.playlists().tracks.add({
            playlistId: data.id,
            trackId: payload?.id
        })

        add(payload, mode);
    }


const selectTracks = (mode) => {
    modals.open('select-tracks', {
        data: {
            items: data.tracks
        },
        callback: (items) => {
            if (!items?.length) {
                return;
            }

            if (mode === 'beginning') {
                items.reverse().forEach(item => addTrack(item, mode));
            } else {
                items.forEach(item => addTrack(item, mode));
            }
        }
    });
}

const selectToBeginning = () => {
    selectTracks('beginning');
}

const selectToEnd = () => {
    selectTracks('end');
}

onMounted(() => {
    data.id = modals.getData(props.name);
    load();
});
</script>

<style lang="scss" scoped>
    @include studio.default-items-list;

    .items-list {
        overflow: auto;
        width: 100%;
        max-width: none;
        padding-right: 0;
        padding-top: 24px;

        .items {
            padding-top: 0;
            li {
                padding: 8px 8px 8px 24px;
                gap: 8px;

                .sort-button {
                    // margin-right: -16px;
                    cursor: grab;
                    fill: var(--white-500);
                }

                &.opened {
                    background: var(--dark-80);
                }
            }
        }
    }
</style>
